import React from "react";
import type { ApolloClient } from "@apollo/client";
import { toast } from "react-toastify";
import { push } from "store/actions/router";
import { find, get, isEmpty } from "lodash";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Col, Row } from "react-bootstrap";
import { stateRequirementsTable } from "statics/stateTaxRequirements";
import { formatCurrency } from "utils/number";
import { createDisbursementRequest } from "actions/withdrawalActions";
import { withdrawalConstants } from "actions/types";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import {
  accountTypesToEnglishMapping,
  distributionTypesToEnglishMapping,
} from "statics/accountTypes";

import Button from "components/Button";
import IconHeader from "components/IconHeader";
import IconSubheader from "components/IconSubheader";

import "pages/dashboards/DashboardLayout.scss";
import Alert from "components/Alert";
import type { RootState } from "types/state";
import type {
  DistributionAmountSelection,
  FederalTaxSelection,
  StateTaxSelection,
} from "./distributionTypes";

interface OwnProps {
  goToNamedStep?: (stepName: string) => void;
  userState?: string;
  selectedAccountType?: string;
  distributionAmountSelection?: DistributionAmountSelection;
  distributionType?: string;
  distributionSpecialReason?: string;
  federalTaxSelection?: FederalTaxSelection;
  stateTaxSelection?: StateTaxSelection;
  isTotalDisbursement?: boolean;
  isRothDistribution?: boolean;
  onComplete?: () => void;
  isAccountClosure?: boolean;
  // react-step-wizard injects stepName at runtime; the workflows declare it
  // on the JSX element.
  stepName?: string;
  // CloseAccountWorkflow passes this; the component does not read it (inert).
  showConfirmationModal?: boolean;
}

const updatingActions = [withdrawalConstants.CREATE_DISBURSEMENT];

const errorSelector = createErrorSelector(updatingActions);
const isUpdating = createLoadingSelector(updatingActions);

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
  const { id, profile: userInfo } = state.user;
  const accounts = state.accounts.iraAccounts;

  return {
    id,
    userInfo,
    ownProps,
    accounts,
    error: errorSelector(state),
    isUpdating: isUpdating(state),
  };
};

const mapDispatchToProps = {
  push,
  createDisbursementRequest,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

type State = Record<string, never>;

class DistributionSummary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {};
  }

  _submitDistribution = async () => {
    const {
      selectedAccountType,
      distributionType,
      distributionSpecialReason,
      federalTaxSelection,
      stateTaxSelection,
      distributionAmountSelection,
      isAccountClosure,
      push,
      onComplete,
    } = this.props;

    if (isAccountClosure) {
      // For account closure, let the parent component handle the submission
      // PRESERVED BUG (conversion-only): onComplete is an optional prop the
      // original calls without a guard — kept via `!` so it still throws if
      // the parent omits it.
      onComplete!();
      return;
    }

    console.log("Starting distribution for account:", selectedAccountType);

    if (!selectedAccountType) {
      console.error("No account type selected");
      toast.error("Please select an account type");
      return;
    }

    try {
      const disbursementInput = {
        accountId: selectedAccountType,
        distributionType,
        distributionSpecialReason,
        federalTaxWithholdingPercentage:
          parseFloat(federalTaxSelection!.percentage!) || 0,
        stateTaxWithholdingAmount: stateTaxSelection!.stateTaxWithholdingAmount
          ? parseFloat(stateTaxSelection!.stateTaxWithholdingAmount)
          : 0,
        stateTaxWithholdingPercentage:
          parseFloat(stateTaxSelection!.stateTaxWithholdingPercentage!) || 0,
        // amount is number|string; the original passes it straight to
        // parseFloat (which coerces it) — cast preserves that behavior.
        distributionAmount: parseFloat(
          distributionAmountSelection!.amount as unknown as string
        ),
        isTotalDisbursement: Boolean(
          distributionAmountSelection!.isTotalDisbursement
        ),
      };

      console.log("Submitting disbursement:", disbursementInput);

      // The dispatched lifecycle resolves to a SUCCESS ({type,data}) or
      // FAILURE ({type,error:string}) action; narrow to the loose shape the
      // original JS relied on (.error is only present on the failure branch).
      // The action's input requires strict literal unions for
      // distributionType/distributionSpecialReason; the original JS passed
      // through whatever strings the parent supplied, so cast at the boundary
      // to preserve that behavior.
      const result = (await this.props.createDisbursementRequest(
        this.props.client as ApolloClient<object>,
        disbursementInput as Parameters<typeof createDisbursementRequest>[1]
      )) as { error?: string } | undefined;
      console.log("Disbursement result:", result);

      if (result && !result.error) {
        toast.success("Successfully processed disbursement request.");
        push("/dashboard");
      } else {
        throw new Error(result!.error || "Disbursement response was invalid");
      }
    } catch (error) {
      console.error("Disbursement error:", error);
      toast.error("There was an error processing your disbursement request.");
      throw error;
    }
  };

  goBackToPreviousStep = () => {
    const { userState, isAccountClosure } = this.props;

    if (isAccountClosure) {
      // PRESERVED BUG (conversion-only): goToNamedStep is an optional prop the
      // original calls without a guard — kept via `!` so it still throws if the
      // parent omits it.
      this.props.goToNamedStep!("accountClosureReason");
      return;
    }

    const stateData = get(stateRequirementsTable, userState as string);

    const isMandatoryOptOut =
      stateData &&
      stateData.option === "Mandatory Opt Out" &&
      stateData.canOptOut;

    if (isMandatoryOptOut) {
      this.props.goToNamedStep!("distributionFederalTax");
    } else {
      this.props.goToNamedStep!("distributionStateTax");
    }
  };

  render() {
    var articleStyle = {
      paddingBottom: 0,
    };

    const {
      selectedAccountType,
      distributionAmountSelection,
      accounts,
      federalTaxSelection,
      stateTaxSelection,
    } = this.props;

    const { percentage } = federalTaxSelection!;
    const {
      stateTaxWithholdingAmount,
      stateTaxWithholdingPercentage,
      stateTaxWithholding,
    } = stateTaxSelection!;

    const distributionAccount = find(accounts, { id: selectedAccountType });

    // PRESERVED BUG (conversion-only): find may return undefined and the
    // original derefs .accountType with `.`, throwing in that case — kept
    // via `!`.
    const accountType = distributionAccount!.accountType;

    const stateTaxType = stateTaxWithholding === "DOLLAR_AMOUNT";
    const stateTaxDollarAmount = isEmpty(stateTaxWithholdingAmount)
      ? "$0"
      : formatCurrency(stateTaxWithholdingAmount!);

    const stateTaxPercentage = isEmpty(stateTaxWithholdingPercentage)
      ? "0%"
      : `${stateTaxWithholdingPercentage}%`;

    const distributionStateTax = stateTaxType
      ? stateTaxDollarAmount
      : stateTaxPercentage;

    const distributionFederalTax = isEmpty(percentage)
      ? "0%"
      : `${percentage}%`;

    const distributionType =
      distributionTypesToEnglishMapping[
        this.props
          .distributionType as keyof typeof distributionTypesToEnglishMapping
      ];

    return (
      <div id="account-selection">
        <div className="mega-container">
          <section className="page-title-wrap">
            <article className="text-cell">
              <IconHeader
                variant="pageHeader"
                headerText="Please review distribution details"
              />
              <IconSubheader
                variant="subheading"
                subheader="By clicking the withdrawal button, you agree that the information is correct. If you have any questions, please contact our Icon Savings Support team."
              />
            </article>
          </section>
          <div>
            <section
              className="form-sec-2col"
              style={{ paddingBottom: 10, paddingTop: 30 }}
            >
              <article className="col-form" style={articleStyle}>
                <div className="account-types">
                  <Row>
                    <Col>
                      <IconHeader
                        variant="labelHeader"
                        headerText="Distribution Type:"
                      />
                    </Col>
                    <Col>
                      <p className="summary-value">{distributionType}</p>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <IconHeader variant="labelHeader" headerText="Account:" />
                    </Col>
                    <Col>
                      <p className="summary-value">
                        {
                          accountTypesToEnglishMapping[
                            accountType as keyof typeof accountTypesToEnglishMapping
                          ]
                        }
                      </p>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <IconHeader variant="labelHeader" headerText="Amount:" />
                    </Col>
                    <Col>
                      <p className="summary-value">
                        {formatCurrency(distributionAmountSelection!.amount!)}
                      </p>
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                      <IconHeader
                        variant="labelHeader"
                        headerText="Federal Tax:"
                      />
                    </Col>
                    <Col>
                      <p className="summary-value">{distributionFederalTax}</p>
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                      <IconHeader
                        variant="labelHeader"
                        headerText="State Tax:"
                      />
                    </Col>
                    <Col>
                      <p className="summary-value">{distributionStateTax}</p>
                    </Col>
                  </Row>
                </div>
                <div
                  className="tax-penalties"
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  <p style={{ paddingBottom: 10, fontWeight: 500 }}>
                    Please read and agree to the following before submitting
                    your distribution request:
                  </p>{" "}
                  <p>
                    I certify that I am authorized to receive payments from this
                    IRA and that all information provided by me is true and
                    accurate. No tax advice has been given to me by the trustee
                    or custodian. All decisions regarding this withdrawal are my
                    own, and I expressly assume responsibility for any
                    consequences that may arise from this withdrawal. I agree
                    that the trustee or custodian is not responsible for any
                    consequences that may arise from processing this withdrawal
                    authorization.
                  </p>
                </div>

                <p
                  style={{
                    paddingTop: 25,
                    fontStyle: "italic",
                    color: "#0a2540",
                    fontSize: 14,
                  }}
                >
                  After submission an Icon Representative will contact you in
                  the next few business days with a Distribution Form for your
                  signature, and additional instructions.
                </p>
              </article>
            </section>
            {this.props.error && <Alert type="error" msg={this.props.error} />}
            <section className="form-sec-2col">
              <article className="col-form">
                <div className="submit-row btn-row">
                  <Button
                    onClick={this.goBackToPreviousStep}
                    name="cancel"
                    btnLabel="Go Back"
                    color="cancel"
                  />
                  <Button
                    style={{ marginLeft: 10 }}
                    name="submit"
                    btnLabel="Submit"
                    onClick={this._submitDistribution}
                    withArrow
                    disabled={this.props.isUpdating}
                  />
                </div>
              </article>
            </section>
          </div>
        </div>
      </div>
    );
  }
}

export default connector(withApollo<Props>(DistributionSummary));
